/* eslint-disable */
import React, { useState, useRef, useEffect } from "react";
import { useHistory, Link } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { authApi } from "../apis/authApi";

const emailrgx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Egyptian phone number validation (after +20)
const egyptianPhoneRegex = /^[1-9][0-9]{8,9}$/; // 9-10 digits after country code, starting with 1-9

const schema = yup.object({
  firstName: yup
    .string()
    .required("First name is required")
    .trim(),
  lastName: yup
    .string()
    .required("Last name is required")
    .trim(),
  email: yup
    .string()
    .matches(emailrgx, "Invalid email")
    .required("Email is required")
    .trim(),
  phone: yup
    .string()
    .optional()
    .nullable()
    .transform((value) => {
      if (!value) return null;
      // Remove +20 prefix if present for validation
      let cleanValue = value;
      if (value.startsWith("+20")) {
        cleanValue = value.substring(3);
      }
      return cleanValue;
    })
    .test(
      "is-valid-egyptian-phone",
      "Please enter a valid Egyptian phone number (9-10 digits after +20)",
      (value) => {
        if (!value) return true; // empty/null is valid
        return egyptianPhoneRegex.test(value);
      },
    ),
  password: yup
    .string()
    .min(8, "Minimum 8 characters")
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords do not match")
    .required("Please confirm your password"),
});

// ─────────────────────────────────────────────────────────────────────────────
const Register = () => {
  const [eyePw, setEyePw] = useState(true);
  const [eyeCpw, setEyeCpw] = useState(true);
  const [loading, setLoading] = useState(false);
  const [regError, setRegError] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const phoneInputRef = useRef(null);
  const history = useHistory();

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
  });

  // Handle phone input with +20 prefix and length limits
  const handlePhoneChange = (e) => {
    let value = e.target.value;

    // Remove any non-digit characters except + at the beginning
    let cleanValue = value.replace(/[^\d+]/g, "");

    // Ensure +20 prefix is always present at the start
    if (!cleanValue.startsWith("+20")) {
      if (cleanValue.startsWith("20")) {
        cleanValue = "+" + cleanValue;
      } else if (cleanValue.startsWith("+")) {
        // Keep as is
      } else {
        cleanValue = "+20" + cleanValue;
      }
    }

    // Extract the number part after +20
    let numberPart = cleanValue.substring(3);

    // Limit to 10 digits max (Egyptian mobile numbers are 10 digits including leading 0? Actually 10-11 total)
    // Egyptian numbers: 10 digits after country code (e.g., 1001234567) or 9 digits for landlines
    if (numberPart.length > 10) {
      numberPart = numberPart.substring(0, 10);
    }

    // Reconstruct the phone number
    let formattedPhone = "+20" + numberPart;

    // Update state and form
    setPhoneValue(formattedPhone);
    setValue("phone", formattedPhone, { shouldValidate: true });

    // Move cursor to the end
    if (phoneInputRef.current) {
      setTimeout(() => {
        phoneInputRef.current.setSelectionRange(
          formattedPhone.length,
          formattedPhone.length,
        );
      }, 0);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setRegError("");

      // Only include phone if it's provided and not empty
      const payload = {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim().toLowerCase(),
        password: data.password,
      };

      if (data.phone && data.phone.trim() !== "" && data.phone !== "+20") {
        payload.phone = data.phone.trim();
      }

      const response = await authApi.register(payload);

      localStorage.setItem("access_token", response.accessToken);
      localStorage.setItem("user", JSON.stringify(response.user));

      history.replace("/reservations/my");
    } catch (error) {
      setRegError(error.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Initialize phone field with +20
  useEffect(() => {
    setPhoneValue("+20");
    setValue("phone", "+20");
  }, [setValue]);

  // ── Eye toggle button ─────────────────────────────────────────────────────
  const EyeBtn = ({ show, onToggle }) => (
    <button
      type="button"
      className="crm-eye"
      onClick={onToggle}
      aria-label={show ? "Show password" : "Hide password"}
    >
      {show ? (
        <svg viewBox="0 0 24 24">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </button>
  );

  // ── Egyptian Flag Component ──
  const EgyptianFlag = () => (
    <div className="crm-flag">
      <svg
        width="24"
        height="18"
        viewBox="0 0 24 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="24" height="6" fill="#CE1126" />
        <rect y="6" width="24" height="6" fill="#FFFFFF" />
        <rect y="12" width="24" height="6" fill="#000000" />
        <circle
          cx="12"
          cy="9"
          r="2.5"
          fill="#B8860B"
          stroke="#D4AF37"
          strokeWidth="0.5"
        />
        <path
          d="M12 7.5 L12.8 8.8 L14.2 8.8 L13.1 9.8 L13.5 11.2 L12 10.3 L10.5 11.2 L10.9 9.8 L9.8 8.8 L11.2 8.8 L12 7.5Z"
          fill="#D4AF37"
        />
      </svg>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { width: 100%; height: 100%; overflow-x: hidden; }

        .crm-root {
          font-family: 'DM Sans', sans-serif;
          min-height: 100vh; width: 100%;
          display: flex; align-items: stretch;
          background: #f0f2f8;
        }

        /* ── Left panel ── */
        .crm-left {
          display: none; position: relative; overflow: hidden;
          background: linear-gradient(150deg, #1a3a5c 0%, #1e5799 50%, #2980b9 100%);
          align-items: center; justify-content: center; flex-direction: column;
          padding: 60px 52px; flex: 1;
        }
        @media (min-width: 900px) { .crm-left { display: flex; } }

        .crm-left-ring { position: absolute; border-radius: 50%; border: 1px solid rgba(255,255,255,0.09); pointer-events: none; }
        .crm-left-ring-a { width: 520px; height: 520px; top: -140px; left: -140px; background: rgba(255,255,255,0.025); }
        .crm-left-ring-b { width: 360px; height: 360px; bottom: -80px; right: -80px; background: rgba(255,255,255,0.03); }
        .crm-left-ring-c { width: 200px; height: 200px; top: 40%; left: 60%; background: rgba(255,255,255,0.02); }
        .crm-left-glow {
          position: absolute; inset: 0; pointer-events: none;
          background:
            radial-gradient(ellipse 60% 40% at 20% 15%, rgba(255,255,255,0.07) 0%, transparent 60%),
            radial-gradient(ellipse 50% 50% at 85% 85%, rgba(26,58,92,0.5) 0%, transparent 60%);
        }
        .crm-left-content { position: relative; z-index: 2; text-align: center; color: #fff; max-width: 320px; }
        .crm-left-icon {
          width: 72px; height: 72px; margin: 0 auto 30px;
          background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.18);
          border-radius: 20px; display: flex; align-items: center; justify-content: center;
          backdrop-filter: blur(12px);
        }
        .crm-left-icon svg { width: 36px; height: 36px; stroke: #fff; fill: none; stroke-width: 1.6; }
        .crm-left-headline { font-family: 'Sora', sans-serif; font-size: 30px; font-weight: 700; line-height: 1.2; letter-spacing: -0.5px; margin-bottom: 14px; }
        .crm-left-desc { font-size: 14.5px; color: rgba(255,255,255,0.6); font-weight: 300; line-height: 1.7; }
        .crm-left-pills { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 40px; justify-content: center; }
        .crm-left-pill {
          background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.14);
          border-radius: 100px; padding: 7px 15px; font-size: 11.5px;
          color: rgba(255,255,255,0.78); font-weight: 500; letter-spacing: 0.4px;
          backdrop-filter: blur(8px);
        }

        /* ── Right panel ── */
        .crm-right {
          display: flex; align-items: center; justify-content: center;
          padding: 40px 24px; background: #f0f2f8; min-height: 100vh; flex: 1;
        }
        @media (min-width: 900px) { .crm-right { flex: 0 0 520px; max-width: 520px; padding: 60px 64px; min-height: auto; } }

        .crm-form-shell {
          width: 100%; max-width: 420px;
          animation: crm-rise 0.48s cubic-bezier(0.22,1,0.36,1) both;
        }
        @keyframes crm-rise { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

        .crm-logo-row { display: flex; align-items: center; gap: 12px; margin-bottom: 36px; }
        .crm-logo-icon {
          width: 40px; height: 40px;
          background: linear-gradient(135deg, #1e5799 0%, #2980b9 100%);
          border-radius: 10px; display: flex; align-items: center; justify-content: center;
        }
        .crm-logo-icon svg { width: 22px; height: 22px; stroke: #fff; fill: none; stroke-width: 2; }
        .crm-logo-name { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; color: #1a1040; letter-spacing: -0.2px; }

        .crm-h1 { font-family: 'Sora', sans-serif; font-size: clamp(22px, 5vw, 28px); font-weight: 700; color: #14103a; letter-spacing: -0.6px; line-height: 1.15; margin-bottom: 6px; }
        .crm-sub { font-size: 14px; color: #7b7897; margin-bottom: 28px; line-height: 1.5; }

        /* Two-column row */
        .crm-row { display: flex; gap: 12px; }
        .crm-row .crm-field { flex: 1; min-width: 0; }

        .crm-error-banner {
          display: flex; align-items: flex-start; gap: 10px;
          background: #fff1f2; border: 1px solid #fecdd3; border-left: 3px solid #f43f5e;
          border-radius: 10px; padding: 12px 14px; margin-bottom: 20px;
          font-size: 13.5px; color: #be123c; font-weight: 500;
          animation: crm-rise 0.25s ease both;
        }
        .crm-error-banner svg { flex-shrink: 0; margin-top: 1px; width: 15px; height: 15px; stroke: #f43f5e; fill: none; stroke-width: 2; }

        .crm-field { margin-bottom: 14px; }
        .crm-label { display: block; font-size: 13px; font-weight: 600; color: #2d2550; margin-bottom: 6px; letter-spacing: 0.15px; }
        .crm-optional { font-size: 11px; color: #9490af; font-weight: 400; margin-left: 4px; }
        .crm-field-inner { 
          position: relative;
          display: flex;
          align-items: center;
        }
        
        /* Phone field with flag */
        .crm-phone-wrapper {
          position: relative;
          flex: 1;
        }
        .crm-flag {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
          z-index: 1;
        }
        .crm-input--phone {
          padding-left: 42px !important;
          font-family: 'DM Sans', monospace;
          letter-spacing: 0.5px;
        }
        
        .crm-input {
          width: 100%; height: 48px; padding: 0 14px;
          font-family: 'DM Sans', sans-serif; font-size: 14.5px; color: #14103a;
          background: #ffffff; border: 1.5px solid #ddd9f3; border-radius: 11px;
          outline: none; transition: border-color 0.2s, box-shadow 0.2s;
          -webkit-appearance: none; appearance: none;
          box-sizing: border-box;
        }
        .crm-input::placeholder { color: #b5b0ce; }
        .crm-input:focus { border-color: #1e5799; box-shadow: 0 0 0 3.5px rgba(30,87,153,0.11); }
        .crm-input--pw { padding-right: 46px; }
        .crm-input--err { border-color: #f43f5e !important; background: #fff8f9 !important; }
        .crm-eye {
          position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer; padding: 5px;
          display: flex; color: #9490af; transition: color 0.18s; line-height: 1;
        }
        .crm-eye:hover { color: #1e5799; }
        .crm-eye svg { width: 17px; height: 17px; stroke: currentColor; fill: none; stroke-width: 2; }
        .crm-field-err { display: block; margin-top: 4px; font-size: 12px; color: #f43f5e; font-weight: 500; }

        .crm-btn {
          display: flex; align-items: center; justify-content: center; gap: 9px;
          width: 100%; height: 50px; margin-top: 20px;
          background: linear-gradient(135deg, #1a3a5c 0%, #2980b9 100%);
          color: #fff; border: none; border-radius: 11px;
          font-family: 'Sora', sans-serif; font-size: 14.5px; font-weight: 600; letter-spacing: 0.2px;
          cursor: pointer; transition: transform 0.15s, box-shadow 0.2s, opacity 0.2s;
          box-shadow: 0 5px 22px rgba(30,87,153,0.32);
        }
        .crm-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 9px 30px rgba(30,87,153,0.42); }
        .crm-btn:disabled { opacity: 0.62; cursor: not-allowed; transform: none !important; }

        .crm-spinner {
          width: 17px; height: 17px;
          border: 2.5px solid rgba(255,255,255,0.3); border-top-color: #fff;
          border-radius: 50%; animation: crm-spin 0.65s linear infinite; flex-shrink: 0;
        }
        @keyframes crm-spin { to { transform: rotate(360deg); } }

        .crm-footer-line {
          margin-top: 24px; padding-top: 20px; border-top: 1px solid #e4e0f5;
          text-align: center; font-size: 13px; color: #7b7897; letter-spacing: 0.1px;
        }
        .crm-footer-line a {
          color: #1e5799; font-weight: 600; text-decoration: none;
          transition: color 0.18s;
        }
        .crm-footer-line a:hover { color: #1a3a5c; text-decoration: underline; }

        .crm-copyright {
          margin-top: 14px; text-align: center;
          font-size: 11.5px; color: #b0aac8; letter-spacing: 0.2px;
        }

        /* Phone number hint */
        .crm-phone-hint {
          font-size: 11px;
          color: #9490af;
          margin-top: 4px;
          display: block;
        }
      `}</style>

      <div className="crm-root">
        {/* ── Left decorative panel ── */}
        <div className="crm-left">
          <div className="crm-left-ring crm-left-ring-a" />
          <div className="crm-left-ring crm-left-ring-b" />
          <div className="crm-left-ring crm-left-ring-c" />
          <div className="crm-left-glow" />
          <div className="crm-left-content">
            <div className="crm-left-icon">
              <svg viewBox="0 0 24 24">
                <path d="M3 17l4-8 4 4 4-6 4 10" />
                <path d="M21 19H3" />
              </svg>
            </div>
            <div className="crm-left-headline">
              Join Boat Cruise
              <br />
              Today
            </div>
            <p className="crm-left-desc">
              Create your account and start booking amazing cruise experiences
              with instant confirmation and QR tickets.
            </p>
            <div className="crm-left-pills">
              <span className="crm-left-pill">Easy Booking</span>
              <span className="crm-left-pill">QR Tickets</span>
              <span className="crm-left-pill">Instant Confirmation</span>
              <span className="crm-left-pill">Track Reservations</span>
            </div>
          </div>
        </div>

        {/* ── Right form panel ── */}
        <div className="crm-right">
          <div className="crm-form-shell">
            <div className="crm-logo-row">
              <div className="crm-logo-icon">
                <svg viewBox="0 0 24 24">
                  <path d="M3 17l4-8 4 4 4-6 4 10" />
                  <path d="M21 19H3" />
                </svg>
              </div>
              <span className="crm-logo-name">Boat Cruise</span>
            </div>

            <h1 className="crm-h1">Create your account</h1>
            <p className="crm-sub">Fill in your details to get started</p>

            {regError && (
              <div className="crm-error-banner" role="alert">
                <svg viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {regError}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* First + Last name */}
              <div className="crm-row">
                <div className="crm-field">
                  <label className="crm-label" htmlFor="reg-first">
                    First Name
                  </label>
                  <div className="crm-field-inner">
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <input
                          id="reg-first"
                          type="text"
                          autoComplete="given-name"
                          placeholder="Ahmed"
                          disabled={loading}
                          className={`crm-input${
                            errors?.firstName ? " crm-input--err" : ""
                          }`}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  {errors?.firstName && (
                    <small className="crm-field-err">
                      {errors.firstName.message}
                    </small>
                  )}
                </div>

                <div className="crm-field">
                  <label className="crm-label" htmlFor="reg-last">
                    Last Name
                  </label>
                  <div className="crm-field-inner">
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <input
                          id="reg-last"
                          type="text"
                          autoComplete="family-name"
                          placeholder="Hassan"
                          disabled={loading}
                          className={`crm-input${
                            errors?.lastName ? " crm-input--err" : ""
                          }`}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  {errors?.lastName && (
                    <small className="crm-field-err">
                      {errors.lastName.message}
                    </small>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="crm-field">
                <label className="crm-label" htmlFor="reg-email">
                  Email Address
                </label>
                <div className="crm-field-inner">
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <input
                        id="reg-email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        disabled={loading}
                        className={`crm-input${
                          errors?.email ? " crm-input--err" : ""
                        }`}
                        {...field}
                      />
                    )}
                  />
                </div>
                {errors?.email && (
                  <small className="crm-field-err">
                    {errors.email.message}
                  </small>
                )}
              </div>

              {/* Phone with Egyptian flag and +20 prefix */}
              <div className="crm-field">
                <label className="crm-label" htmlFor="reg-phone">
                  Phone <span className="crm-optional">(optional)</span>
                </label>
                <div className="crm-field-inner">
                  <div className="crm-phone-wrapper">
                    <EgyptianFlag />
                    <input
                      id="reg-phone"
                      type="tel"
                      ref={phoneInputRef}
                      value={phoneValue}
                      onChange={handlePhoneChange}
                      autoComplete="tel"
                      placeholder="+20 100 123 4567"
                      disabled={loading}
                      className={`crm-input crm-input--phone${
                        errors?.phone ? " crm-input--err" : ""
                      }`}
                    />
                  </div>
                </div>
                <small className="crm-phone-hint">
                  Format: +20 followed by 9-10 digits (e.g., +201001234567)
                </small>
                {errors?.phone && (
                  <small className="crm-field-err">
                    {errors.phone.message}
                  </small>
                )}
              </div>

              {/* Password */}
              <div className="crm-field">
                <label className="crm-label" htmlFor="reg-pw">
                  Password
                </label>
                <div className="crm-field-inner">
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <>
                        <input
                          id="reg-pw"
                          type={eyePw ? "password" : "text"}
                          autoComplete="new-password"
                          placeholder="Min. 8 characters"
                          disabled={loading}
                          className={`crm-input crm-input--pw${
                            errors?.password ? " crm-input--err" : ""
                          }`}
                          {...field}
                        />
                        <EyeBtn
                          show={eyePw}
                          onToggle={() => setEyePw((v) => !v)}
                        />
                      </>
                    )}
                  />
                </div>
                {errors?.password && (
                  <small className="crm-field-err">
                    {errors.password.message}
                  </small>
                )}
              </div>

              {/* Confirm password */}
              <div className="crm-field">
                <label className="crm-label" htmlFor="reg-cpw">
                  Confirm Password
                </label>
                <div className="crm-field-inner">
                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                      <>
                        <input
                          id="reg-cpw"
                          type={eyeCpw ? "password" : "text"}
                          autoComplete="new-password"
                          placeholder="Repeat your password"
                          disabled={loading}
                          className={`crm-input crm-input--pw${
                            errors?.confirmPassword ? " crm-input--err" : ""
                          }`}
                          {...field}
                        />
                        <EyeBtn
                          show={eyeCpw}
                          onToggle={() => setEyeCpw((v) => !v)}
                        />
                      </>
                    )}
                  />
                </div>
                {errors?.confirmPassword && (
                  <small className="crm-field-err">
                    {errors.confirmPassword.message}
                  </small>
                )}
              </div>

              <button type="submit" className="crm-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="crm-spinner" />
                    Creating account…
                  </>
                ) : (
                  "Create Account"
                )}
              </button>
            </form>

            <div className="crm-footer-line">
              Already have an account? <Link to="/login">Sign in</Link>
            </div>
            <div className="crm-footer-line" style={{ marginTop: 8 }}>
              Just want to book?{" "}
              <Link
                to="/book"
                style={{
                  color: "#1e5799",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Book as Guest
              </Link>
            </div>
            <div className="crm-copyright">© Bee Interactive copy right</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
